import { useState } from 'react';
import { Box, IconButton, Button, Grid2, Typography } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import EditIcon from '@mui/icons-material/Edit';
import CustomDivider from '../../shared/utils/CustomDivider';
import TableFilterTemplate from '../../shared/utils/TableFilterTemplate';
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent';
import ToggleSwitch from '../../shared/utils/CustomToggle';
import { Grid_Gap, Input_Width } from '../../shared/constants';
import { TextFormField } from '../../shared/components/formfields/TextFormField';
import { useGetGrades, useCreateGrade, useUpdateGrade, useUpdateGradeStatus } from '../../assets/api/useMastersApi';

const GradeMaster = () => {
    const [selectedItem, setSelectedItem] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    
    const { data: gradesData, isLoading } = useGetGrades({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize
    });
    
    const gradesList = Array.isArray(gradesData?.data) ? gradesData.data : (Array.isArray(gradesData) ? gradesData : []);

    // Add sr_no dynamically if not present
    const formattedRows = gradesList.map((row, index) => ({
        ...row,
        sr_no: row.sr_no || (index + 1) + (paginationModel.page * paginationModel.pageSize)
    }));

    const totalRows = gradesData?.pagination?.totalElements 
        || gradesData?.pagination?.total 
        || gradesData?.total 
        || gradesData?.data?.total 
        || gradesData?.totalRecords 
        || gradesData?.count
        || gradesData?.data?.count
        || gradesList.length;

    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Grade name is required'),
    });

    const initialValues = {
        name: selectedItem?.grade_name || '',
    };

    const createMutation = useCreateGrade(() => {
        setIsEditing(false);
        setSelectedItem(null);
    });

    const updateMutation = useUpdateGrade(() => {
        setIsEditing(false);
        setSelectedItem(null);
    });

    const statusMutation = useUpdateGradeStatus();

    const handleSubmit = (values, { resetForm }) => {
        if (isEditing) {
            updateMutation.mutate({ id: selectedItem.id, payload: values });
        } else {
            createMutation.mutate(values);
            resetForm();
        }
    };

    const columns = [
        { field: 'sr_no', headerName: 'Sr No', flex: 0.5, renderCell: (params) => <Typography>{params.value || (params.api.getAllRowIds().indexOf(params.id) + 1) + (paginationModel.page * paginationModel.pageSize)}</Typography> },
        { field: 'grade_name', headerName: 'Grade Name', flex: 2 },
        {
            field: 'actionsEdit',
            headerName: 'Edit',
            flex: 1,
            renderCell: (params) => (
                <IconButton
                    onClick={() => {
                        setSelectedItem(params.row);
                        setIsEditing(true);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    size="small"
                >
                    <EditIcon />
                </IconButton>
            ),
        },
        {
            field: 'actionsToggle',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => (
                <ToggleSwitch 
                    checked={params.row.status === true || params.row.status === 1} 
                    onCheck={() => {
                        const newStatus = !(params.row.status === true || params.row.status === 1);
                        statusMutation.mutate({ id: params.row.id, payload: { status: newStatus } });
                    }} 
                />
            ),
        },
    ];

    return (
        <Box>
            <CustomDivider />
            <NormalContainerWithHeader headerText={isEditing ? 'Edit Grade' : 'Add Grade'}>
                <Formik 
                    initialValues={initialValues} 
                    validationSchema={validationSchema} 
                    onSubmit={handleSubmit} 
                    enableReinitialize
                >
                    {({ resetForm }) => (
                        <Form>
                            <Grid2 container spacing={Grid_Gap}>
                                <Grid2 size={Input_Width}>
                                    <Field name="name" component={TextFormField} label="Grade Name" isRequired />
                                </Grid2>
                            </Grid2>
                            <CustomDivider />
                            <Grid2 container spacing={Grid_Gap} sx={{ justifyContent: "end" }}>
                                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            resetForm();
                                            setIsEditing(false);
                                            setSelectedItem(null);
                                        }}
                                        fullWidth
                                    >
                                        {isEditing ? 'Cancel' : 'Reset'}
                                    </Button>
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        fullWidth
                                        disabled={createMutation.isPending || updateMutation.isPending}
                                    >
                                        {isEditing ? 'Update' : 'Submit'}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </Form>
                    )}
                </Formik>
            </NormalContainerWithHeader>

            <CustomDivider />

            <TableFilterTemplate
                title="Grade List"
                columns={columns}
                rowsData={formattedRows}
                isLoading={isLoading} 
                isServerSide={true}
                totalServerRows={totalRows}
                onServerPaginationChange={setPaginationModel}
            />
        </Box>
    );
};

export default GradeMaster;
